import { useQuery } from "@apollo/client";
import moment from "moment";
import { useEffect, useMemo, useRef } from "react";
import {
  GetCalendarMeetingsQuery,
  GetCalendarMeetingsQueryVariables,
} from "types/graphql-schema";

import { currentUserVar } from "@cache/cache";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import client from "@graphql/client";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

import Week from "./components/week/week";
import getCalendarMeetings from "./graphql/get-calendar-meetings-query";

export const topPixelMargin = 30;
export const minimumEventHeight = 28;
export const hourInPixels = 75;
export const minuteInPixels = hourInPixels / 60;

const Calendar = ({
  selectedDay,
  onClickMeetingLink,
}: {
  selectedDay: string;
  onClickMeetingLink: (meetingId: number, meetingGroupId: number) => void;
}) => {
  // HOOKS
  useDocumentTitle("Calendar");
  const currentUser = currentUserVar();

  const firstDayOfWeek = moment(selectedDay).startOf("isoWeek").format();
  const lastDayOfWeek = moment(selectedDay).endOf("isoWeek").format();
  const startDatetimeGte = firstDayOfWeek;
  const startDatetimeLte = lastDayOfWeek;
  const currentUserId = currentUser.id;

  const lastSelectedDayRef = useRef(selectedDay);

  const variables = useMemo(
    () => ({
      forUserId: currentUserId,
      startDatetime_Gte: startDatetimeGte,
      startDatetime_Lte: startDatetimeLte,
      participants: [currentUserId],
    }),
    [startDatetimeGte, startDatetimeLte, currentUserId],
  );

  const { data, fetchMore } = useQuery<
    GetCalendarMeetingsQuery,
    GetCalendarMeetingsQueryVariables
  >(getCalendarMeetings, {
    variables,
  });

  useEffect(() => {
    // determine the direction of valendar navigation and prefetch the events
    const direction =
      selectedDay > lastSelectedDayRef.current ? "forward" : "backward";
    if (direction === "backward") {
      const firstDayOfPreviousWeek = moment(selectedDay)
        .subtract(1, "week")
        .startOf("isoWeek")
        .format();
      const lastDayOfPreviousWeek = moment(selectedDay)
        .subtract(1, "week")
        .endOf("isoWeek")
        .format();
      client.query<GetCalendarMeetingsQuery, GetCalendarMeetingsQueryVariables>(
        {
          query: getCalendarMeetings,
          variables: {
            ...variables,
            startDatetime_Gte: firstDayOfPreviousWeek,
            startDatetime_Lte: lastDayOfPreviousWeek,
          },
        },
      );
    }
    if (direction === "forward") {
      const firstDayOfNextWeek = moment(selectedDay)
        .add(1, "week")
        .startOf("isoWeek")
        .format();
      const lastDayOfNextWeek = moment(selectedDay)
        .add(1, "week")
        .endOf("isoWeek")
        .format();
      client.query<GetCalendarMeetingsQuery, GetCalendarMeetingsQueryVariables>(
        {
          query: getCalendarMeetings,
          variables: {
            ...variables,
            startDatetime_Gte: firstDayOfNextWeek,
            startDatetime_Lte: lastDayOfNextWeek,
          },
        },
      );
    }
    lastSelectedDayRef.current = selectedDay;
  }, [variables]);

  // fetch more if data has multiple pages
  useEffect(() => {
    if (
      data &&
      data.calendar &&
      data.calendar.totalCount > data.calendar.edges.length
    ) {
      fetchMore({
        variables: {
          startDatetime_Gte: startDatetimeGte,
          endDatetime_Lte: startDatetimeLte,
          after: data.calendar.pageInfo.endCursor,
          merge: true,
        },
      });
    }
  }, [data]);

  const meetings = data?.calendar ? assertEdgesNonNull(data.calendar) : [];

  // RENDER
  return (
    <div
      aria-label="Calendar container"
      className={classNames(
        "@container/calendar fs-unmask",
        "flex h-full flex-col bg-red-200",
      )}
    >
      <div className={classNames("flex h-full flex-col bg-white")}>
        <Week
          meetings={meetings}
          selectedDay={selectedDay}
          onClickMeetingLink={onClickMeetingLink}
        />
      </div>
    </div>
  );
};

export default Calendar;
